import React, { useEffect, useState, useCallback } from 'react';
import { api, type ProfileListItem } from '../lib/api';
import { fmtDate } from '../lib/format';
import { OperaWindow, OperaSection } from './OperaWindow';
import { NewProfileModal } from './NewProfileModal';
import { useAuth } from '../lib/auth';

interface Props {
  onClose: () => void;
  onSelect: (profileId: string) => void;
}

export function ProfileSearchModal({ onClose, onSelect }: Props) {
  const { user } = useAuth();
  const canCreate = user?.role === 'MANAGER' || user?.role === 'ADMIN';
  const [showNew, setShowNew] = useState(false);
  const [query, setQuery] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [results, setResults] = useState<ProfileListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.profiles(query.trim(), includeArchived);
      setResults(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [query, includeArchived]);

  // Initial load + debounced reload when query changes
  useEffect(() => {
    const t = setTimeout(load, query ? 250 : 0);
    return () => clearTimeout(t);
  }, [load]);

  if (showNew) {
    return (
      <NewProfileModal
        onClose={() => setShowNew(false)}
        onCreated={(profileId) => { setShowNew(false); onSelect(profileId); }}
      />
    );
  }

  return (
    <OperaWindow
      title="PROFILE SEARCH"
      onClose={onClose}
      size="large"
      sideButtons={[
        ...(canCreate ? [{ label: 'New', onClick: () => setShowNew(true), primary: true }] : []),
        { label: 'Refresh', onClick: load },
        { label: 'Close',   onClick: onClose },
      ]}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Search">
        <div className="profile-search-bar">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, phone, email, or alias…"
            autoFocus
          />
          <label style={{ marginLeft: 12, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={e => setIncludeArchived(e.target.checked)}
            />
            Include archived
          </label>
        </div>
      </OperaSection>

      <div className="profile-search-results">
        {loading && <div className="loading">Loading…</div>}
        {!loading && results.length === 0 && (
          <div className="loading">No profiles match{query ? ` "${query}"` : ''}.</div>
        )}
        {!loading && results.length > 0 && (
          <table className="profile-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Stays</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {results.map(p => (
                <tr
                  key={p.id}
                  onClick={() => onSelect(p.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter') onSelect(p.id); }}
                >
                  <td>
                    <strong>{p.workingName}</strong>
                    {p.aliases.length > 0 && (
                      <span style={{ color: 'var(--text-secondary)', fontSize: 11, marginLeft: 6 }}>
                        aka {p.aliases.join(', ')}
                      </span>
                    )}
                  </td>
                  <td>{p.phone || '—'}</td>
                  <td>{p.email || '—'}</td>
                  <td>{p.stayCount}</td>
                  <td>
                    {p.isArchived
                      ? <span style={{ color: 'var(--accent-red)' }}>Archived</span>
                      : <span style={{ color: 'var(--accent-green)' }}>Active</span>}
                  </td>
                  <td>{fmtDate(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
        {results.length > 0 && `${results.length} profile${results.length === 1 ? '' : 's'} shown.`}
      </div>
    </OperaWindow>
  );
}
